import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'

const PickerModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  type = 'header', // 'header' or 'align'
  title = 'Options'
}) => {
  
  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  // Handle selection
  const handleSelect = (value) => {
    onSelect(value)
    onClose()
  }

  if (!isOpen) return null

  // Header options
  const headerOptions = [
    { value: 1, label: 'Heading 1', icon: 'H1' },
    { value: 2, label: 'Heading 2', icon: 'H2' },
    { value: 3, label: 'Heading 3', icon: 'H3' },
    { value: false, label: 'Normal', icon: 'P' }
  ]

  // Alignment options
  const alignOptions = [
    { value: '', label: 'Left Align', icon: '⇤' },
    { value: 'center', label: 'Center', icon: '⇥' },
    { value: 'right', label: 'Right Align', icon: '⇥' },
    { value: 'justify', label: 'Justify', icon: '⇤⇥' }
  ]

  const options = type === 'header' ? headerOptions : alignOptions

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 animate-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Options List */}
        <div className="p-4">
          <div className="space-y-2">
            {options.map((option, index) => (
              <button
                key={index}
                type="button"
                className="w-full flex items-center gap-3 p-3 text-left rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => handleSelect(option.value)}
              >
                <div className="w-8 h-8 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {option.icon}
                </div>
                <span className="text-gray-900 dark:text-gray-100">
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(modalContent, document.body)
}

export default PickerModal
